import React, { useState } from 'react';
import axios from 'axios';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TRAINING_ROLES = [
  ['FrontDesk & Billing', 'Front desk, patient coordination, and billing'],
  ['Director & Corporate', 'Directors and corporate staff'],
  ['Clinical Team', 'Dentists, hygienists, and dental assistants'],
  ['Credentialing Specialist', 'Credentialing and provider enrollment'],
  ['Office Team Lead', 'Office managers and team leads'],
  ['Regional Manager', 'Regional managers'],
];

export default function IntakeForm({ startupError = '' }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState(startupError);
  const [submitting, setSubmitting] = useState(false);
  const [requested, setRequested] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!fullName.trim() || !EMAIL_PATTERN.test(normalizedEmail) || !role) {
      setError('Enter your name, email, and training role.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        '/api/access/request',
        { fullName: fullName.trim(), email: normalizedEmail, requestedRole: role },
        { headers: { 'x-training-request': '1' }, withCredentials: true }
      );
      setRequested(true);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        'Training access is temporarily unavailable. Please try again in a few minutes.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div style={{ marginBottom: 28 }}>
        <div className="step-badge"><span className="step-badge-dot" />Secure access</div>
        <h1 className="page-title">HIPAA Compliance Training</h1>
        <p className="page-subtitle">
          Enter your name, email, and the training role that matches your work. We will send a secure
          link directly to that address. You do not need a TrinityHR account to begin.
        </p>
      </div>

      <div className="card">
        {requested ? (
          <div className="access-request-result" role="status" aria-live="polite">
            <div className="access-request-icon" aria-hidden="true">✉</div>
            <h2>Request received</h2>
            <p className="access-request-summary">
              Your secure training link is on its way to the email address you entered. It does not
              require a TrinityHR account.
            </p>
            <div className="access-recovery">
              <strong>If the link is not there within two minutes:</strong>
              <ol>
                <li>Check the exact email address you entered.</li>
                <li>Check spam or junk for “Odyssey Solutions HIPAA Training.”</li>
                <li>Ask your office manager to confirm your name, role, and email were entered correctly.</li>
              </ol>
            </div>
            <button className="btn btn-outline" onClick={() => setRequested(false)}>
              Try a different email
            </button>
          </div>
        ) : (
          <>
            <div className="info-box">
              <p>
                <strong>You can start training before your TrinityHR profile is ready.</strong>{' '}
                Your name and role below will appear on the training record. Your office manager can
                match the completed record to HR afterward.
              </p>
            </div>

            {error && (
              <div style={{ background: '#FFF5F5', border: '1px solid #FEB2B2', color: '#C53030', borderRadius: 7, padding: '12px 14px', marginBottom: 20, fontSize: 13 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="full-name">Full name for your certificate <span>*</span></label>
                <input
                  id="full-name"
                  name="fullName"
                  type="text"
                  className="form-input"
                  placeholder="Your full name"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  autoComplete="name"
                  autoFocus
                  disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email for your training link <span>*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="form-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  disabled={submitting}
                />
                <span className="form-hint">
                  Use an email you can open now. A personal email is fine.
                </span>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="training-role">Your training role <span>*</span></label>
                <select
                  id="training-role"
                  name="requestedRole"
                  className="form-select"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  required
                  disabled={submitting}
                >
                  <option value="">Choose the closest match</option>
                  {TRAINING_ROLES.map(([value, description]) => (
                    <option key={value} value={value}>{description}</option>
                  ))}
                </select>
                <span className="form-hint">Not sure which fits? Ask your office manager before requesting the link.</span>
              </div>
              <div style={{ marginTop: 28 }}>
                <button type="submit" className="btn btn-primary btn--full btn--lg" disabled={submitting}>
                  {submitting ? 'Sending secure link…' : 'Email My Training Link →'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
        {[
          { icon: '🔒', text: 'One-time secure link' },
          { icon: '📋', text: 'No HR account needed' },
          { icon: '🏆', text: 'Auditable certificate' },
        ].map(({ icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#546E7A' }}>
            <span style={{ fontSize: 18 }}>{icon}</span>{text}
          </div>
        ))}
      </div>
    </div>
  );
}
